import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const PAGE = {
  url: 'https://stellarreply.com/for-airbnb-hosts',
  title: 'Airbnb Review Management Software for Hosts | StellarReply',
  metaDescription: 'AI review management built for Airbnb hosts: warm host-voice replies to every guest review, listing optimisation from your own reviews, guest review writing and competitor tracking. Flat £20/month, 14-day free trial.',
  ogDescription: 'Reply to every guest review in your own voice, optimise your listing using guest language, and track competing listings. Built for Airbnb hosts.',
};

const FAQS = [
  {
    q: 'Does StellarReply post replies to Airbnb automatically?',
    a: 'Airbnb does not offer hosts an API for posting review responses, so no tool can post them for you. StellarReply drafts every reply in your voice so all you do is copy and paste it into your Airbnb dashboard. The writing, which is the part that takes the time, is done.',
  },
  {
    q: 'Do I need to connect my Airbnb account?',
    a: 'No. There is nothing to connect and no password to share. You paste your guest reviews in (individually or in bulk) and add competitor listings by their public URL.',
  },
  {
    q: 'What is Listing Intelligence?',
    a: 'It reads all your guest reviews and writes an optimised listing description using the exact words your guests use, plus five title suggestions, an amenity action list and a pricing confidence signal. Guests tell you what sells your place, and your listing should say it in their words.',
  },
  {
    q: 'Can Airbnb remove an unfair review?',
    a: "Only if it breaks Airbnb's Reviews Policy: for example the guest threatened a bad review to get a refund, never actually stayed, or left it in retaliation after being told they broke a rule. Harsh but genuine reviews stay up, including ones about things outside your control. Our free removal check tells you whether yours qualifies and under which rule.",
  },
  {
    q: 'How much does it cost?',
    a: 'A flat £20/month (USD supported) with a 14-day free trial and no credit card required to start. No credits, no per-reply charges, cancel anytime.',
  },
];

// SEO meta tags - set on mount (same pattern as the free tool pages)
function useMetaTags() {
  useEffect(() => {
    document.title = PAGE.title;
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(name.startsWith('og:') ? 'property' : 'name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', PAGE.metaDescription);
    setMeta('og:title', PAGE.title);
    setMeta('og:description', PAGE.ogDescription);
    setMeta('og:type', 'website');
    setMeta('og:url', PAGE.url);

    // Self-canonical — the SPA shell ships the homepage canonical
    const canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute('href') : null;
    if (canonical) canonical.setAttribute('href', PAGE.url);

    const schema = document.createElement('script');
    schema.type = 'application/ld+json';
    schema.id = 'airbnb-hosts-schema';
    schema.textContent = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "StellarReply for Airbnb Hosts",
        "url": PAGE.url,
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web",
        "description": PAGE.ogDescription,
        "offers": { "@type": "Offer", "price": "20", "priceCurrency": "GBP" }
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": FAQS.map(f => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a }
        }))
      }
    ]);
    document.head.appendChild(schema);
    return () => {
      if (canonical && prevCanonical) canonical.setAttribute('href', prevCanonical);
      document.getElementById('airbnb-hosts-schema')?.remove();
    };
  }, []);
}

const FEATURES = [
  {
    icon: '✉',
    title: 'Host-voice review replies',
    text: 'AI drafts a warm, personal response to every guest review. Hosting language ("guest", "stay", "the flat"), no corporate cliches, never starts with "Thank you for". You copy, paste, done.',
  },
  {
    icon: '✨',
    title: 'Listing Intelligence',
    text: 'Reads all your reviews and rewrites your listing description in your guests’ own words, plus 5 title suggestions, an amenity action list and a pricing confidence signal.',
  },
  {
    icon: '✍',
    title: 'Guest Review Writer',
    text: 'Rate your guest on three sliders, add a note, and get a thoughtful host-to-guest review in seconds. Never skip reviewing a guest again.',
  },
  {
    icon: '🔍',
    title: 'Competitor tracking',
    text: 'Paste any competing listing’s URL. We pull its rating, category scores and what guests praise, and show you exactly where you win and where you leak bookings.',
  },
  {
    icon: '📋',
    title: 'Bulk review import',
    text: 'Paste a whole page of reviews from your hosting dashboard. AI splits them into individual reviews with names, dates and ratings. 30 reviews in one paste.',
  },
  {
    icon: '📈',
    title: 'Rating protection',
    text: 'Category-level tracking against the 4.8 Superhost benchmark, so a slipping check-in score gets fixed before it costs you the badge.',
  },
];

export default function ForAirbnbHostsPage() {
  const navigate = useNavigate();
  useMetaTags();

  const startTrial = () => navigate('/login?register=true&category=hospitality');

  return (
    <div style={styles.page}>
      <style>{cssText}</style>

      {/* Nav */}
      <nav style={styles.nav}>
        <div style={styles.navInner}>
          <div style={styles.logo} onClick={() => navigate('/')}>
            Stellar<span style={{ color: '#16a34a' }}>Reply</span>
          </div>
          <button style={styles.navCta} onClick={startTrial}>
            Start free trial
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section style={styles.hero}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <div style={styles.badge}>Built for Airbnb &amp; holiday-let hosts</div>
          <h1 style={styles.h1}>
            Every guest review,<br />
            <span style={{ color: '#16a34a' }}>working for your listing</span>
          </h1>
          <p style={styles.heroSub}>
            StellarReply is review management built for hosts: AI replies in your voice,
            a listing description written from your guests&apos; own words, guest reviews in
            seconds, and competitor listings tracked, all for a flat &#163;20/month.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button style={{ ...styles.btn, width: 'auto', padding: '14px 32px' }} onClick={startTrial}>
              Start 14-day free trial
            </button>
            <button style={styles.btnGhost} onClick={() => navigate('/free-airbnb-listing-audit')}>
              Run a free listing audit first
            </button>
          </div>
          <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 14 }}>
            No credit card required &middot; No Airbnb login needed &middot; Cancel anytime
          </p>
        </div>
      </section>

      {/* Features */}
      <section style={{ padding: '40px 20px' }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          <h2 style={styles.sectionTitle}>Everything your reviews should be doing for you</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16 }}>
            {FEATURES.map(f => (
              <div key={f.title} style={styles.card}>
                <div style={{ fontSize: 22, marginBottom: 8 }}>{f.icon}</div>
                <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 8px', color: '#1a1a1a' }}>{f.title}</h3>
                <p style={styles.cardText}>{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section style={{ padding: '20px 20px 40px' }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <h2 style={styles.sectionTitle}>How it works</h2>
          {[
            ['Add your reviews', 'Paste guest reviews in bulk from your hosting dashboard, or add them one by one. No account connection, no password sharing.'],
            ['The AI learns your voice', 'Tell it your tone once. Every reply sounds like you on a good day: warm, specific, and never like a hotel chain.'],
            ['Copy, paste, improve', 'Replies are ready to paste into Airbnb. Listing Intelligence and competitor tracking turn the same reviews into more bookings.'],
          ].map(([title, text], i) => (
            <div key={title} style={{ display: 'flex', gap: 16, marginBottom: 18, textAlign: 'left' }}>
              <div style={styles.stepNumber}>{i + 1}</div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a1a' }}>{title}</div>
                <p style={{ ...styles.cardText, margin: '2px 0 0' }}>{text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Free tools strip */}
      <section style={{ padding: '20px' }}>
        <div style={{ ...styles.card, maxWidth: 680, margin: '0 auto', textAlign: 'center', border: '1px solid rgba(22,163,74,0.3)' }}>
          <h2 style={{ fontSize: 17, fontWeight: 800, margin: '0 0 8px', color: '#1a1a1a' }}>Try it free, no signup</h2>
          <p style={{ ...styles.cardText, marginBottom: 14 }}>
            Get a feel for the AI with our free tools for hosts.
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button style={styles.btnGhost} onClick={() => navigate('/airbnb-superhost-checker')}>
              Are you above 4.8?
            </button>
            <button style={styles.btnGhost} onClick={() => navigate('/free-airbnb-listing-audit')}>
              Free Airbnb listing audit
            </button>
            <a href="/airbnb-rating-calculator" style={{ ...styles.btnGhost, textDecoration: 'none', display: 'inline-block' }}>
              Rating calculator
            </a>
            <a href="/airbnb-superhost-assessment-dates" style={{ ...styles.btnGhost, textDecoration: 'none', display: 'inline-block' }}>
              Next assessment date
            </a>
            <button style={styles.btnGhost} onClick={() => navigate('/free-airbnb-review-response-generator')}>
              Free review reply generator
            </button>
            <a href="/remove-unfair-review" style={{ ...styles.btnGhost, textDecoration: 'none', display: 'inline-block' }}>
              Can this review be removed?
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ padding: '30px 20px 20px' }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <h2 style={styles.sectionTitle}>Questions hosts ask</h2>
          {FAQS.map(f => (
            <div key={f.q} style={{ ...styles.card, textAlign: 'left' }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 8px', color: '#1a1a1a' }}>{f.q}</h3>
              <p style={styles.cardText}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Guides */}
      <section style={{ padding: '10px 20px 40px' }}>
        <div style={{ ...styles.card, maxWidth: 680, margin: '0 auto', textAlign: 'left' }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 10px', color: '#1a1a1a' }}>Guides for hosts</h2>
          {[
            ['/blog/airbnb-review-response-templates.html', 'Airbnb review response templates for hosts'],
            ['/blog/airbnb-review-response-templates.html', 'How to respond to a bad Airbnb review'],
            ['/blog/airbnb-superhost-requirements.html', 'Airbnb Superhost requirements: the four numbers that matter'],
            ['/blog/how-to-improve-airbnb-rating.html', 'How to improve your Airbnb rating, category by category'],
            ['/blog/optimise-airbnb-listing-using-guest-reviews.html', 'Optimise your listing using guest reviews'],
            ['/blog/airbnb-guest-review-templates-that-work.html', 'Guest review templates that get 5 stars back'],
          ].map(([href, label]) => (
            <a key={href} href={href} style={{ display: 'block', padding: '5px 0', fontSize: 14, color: '#16a34a', fontWeight: 600, textDecoration: 'none' }}>
              {label}
            </a>
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section style={{ padding: '10px 20px 60px', textAlign: 'center' }}>
        <div style={{ ...styles.ctaCard, maxWidth: 680, margin: '0 auto' }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 8px', color: '#1a1a1a' }}>
            Your reviews are your best marketing. Put them to work.
          </h2>
          <p style={{ ...styles.cardText, marginBottom: 16 }}>
            Flat &#163;20/month. 14-day free trial, no credit card, cancel anytime.
          </p>
          <button style={{ ...styles.btn, width: 'auto', padding: '14px 32px' }} onClick={startTrial}>
            Start free trial
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <span>Powered by </span>
        <a href="https://stellarreply.com" style={{ color: '#16a34a', textDecoration: 'none', fontWeight: 600 }}>
          StellarReply
        </a>
      </footer>
    </div>
  );
}

const cssText = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&display=swap');
`;

const styles = {
  page: { fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif", minHeight: '100vh', background: '#faf9f6' },
  nav: { borderBottom: '1px solid #e8e5df', background: '#fff' },
  navInner: { maxWidth: 900, margin: '0 auto', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  logo: { fontSize: 18, fontWeight: 800, color: '#1a1a1a', cursor: 'pointer' },
  navCta: { background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans', system-ui, sans-serif" },
  hero: { padding: '60px 20px 30px', textAlign: 'center' },
  badge: { display: 'inline-block', fontSize: 12, fontWeight: 600, color: '#16a34a', background: 'rgba(22,163,74,0.08)', padding: '4px 14px', borderRadius: 20, marginBottom: 16 },
  h1: { fontSize: 38, fontWeight: 800, color: '#1a1a1a', lineHeight: 1.2, margin: '0 0 16px' },
  heroSub: { fontSize: 15, color: '#6b7280', lineHeight: 1.6, margin: '0 0 28px', maxWidth: 560, marginLeft: 'auto', marginRight: 'auto' },
  sectionTitle: { fontSize: 22, fontWeight: 800, color: '#1a1a1a', textAlign: 'center', margin: '0 0 24px' },
  btn: { padding: '14px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #16a34a, #059669)', color: '#fff', fontSize: 15, fontWeight: 800, cursor: 'pointer', fontFamily: "'DM Sans', system-ui, sans-serif" },
  btnGhost: { padding: '12px 24px', borderRadius: 10, border: '1px solid #d4d1cc', background: '#fff', color: '#1a1a1a', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', system-ui, sans-serif" },
  card: { background: '#fff', borderRadius: 12, border: '1px solid #e8e5df', padding: '20px 24px', marginBottom: 14 },
  cardText: { fontSize: 14, color: '#6b7280', lineHeight: 1.7, margin: 0 },
  stepNumber: { minWidth: 28, height: 28, borderRadius: '50%', background: 'rgba(22,163,74,0.1)', color: '#16a34a', fontWeight: 800, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 },
  ctaCard: { background: '#fff', borderRadius: 12, border: '2px solid #16a34a', padding: '32px 28px' },
  footer: { textAlign: 'center', padding: '20px', fontSize: 12, color: '#9ca3af', borderTop: '1px solid #e8e5df' },
};
